import {OrderIntakeOption, Plant} from '../../../declarations/types'

import {Contract, SplitLoadMaterial} from './types'

/**
 * Extracts unique plants from selectedSite materials
 * Filters out plants that only have invalid materials
 * @param selectedSite - The selected site containing materials dictionary
 * @returns Array of unique Plant objects with at least one valid material
 */
export const extractPlantsFromSite = (selectedSite: OrderIntakeOption): Plant[] => {
  const plantsMap = new Map<string, {plant: Plant; hasValidMaterial: boolean}>()

  // Iterate through all materials to collect unique plants
  Object.values(selectedSite.materials).forEach((materialOptions) => {
    materialOptions.forEach((materialOption) => {
      const plant = materialOption.plant
      // Use plantNumber as the unique key
      if (!plantsMap.has(plant.plantNumber)) {
        plantsMap.set(plant.plantNumber, {
          plant: plant,
          hasValidMaterial: !materialOption.invalid
        })
      } else {
        // Update hasValidMaterial if we find at least one valid material
        const existing = plantsMap.get(plant.plantNumber)!
        if (!materialOption.invalid) {
          existing.hasValidMaterial = true
        }
      }
    })
  })

  // Convert map values to array, filter out plants with only invalid materials, and sort
  return Array.from(plantsMap.values())
    .filter((entry) => entry.hasValidMaterial)
    .map((entry) => entry.plant)
    .sort((a, b) => a.plantName.localeCompare(b.plantName))
}

/**
 * Extracts unique contracts from selectedSite materials
 * Filters out contracts that only have invalid materials
 * @param selectedSite - The selected site containing materials dictionary
 * @returns Array of unique Contract objects with at least one valid material
 */
export const extractContractsFromSite = (selectedSite: OrderIntakeOption): Contract[] => {
  const contractsMap = new Map<string, {contract: Contract; hasValidMaterial: boolean}>()

  // Iterate through all materials to collect unique contracts
  Object.values(selectedSite.materials).forEach((materialOptions) => {
    materialOptions.forEach((materialOption) => {
      const material = materialOption.material
      // Create a unique key from contractNumber
      const contractKey = `${material.contractNumber}`

      if (!contractsMap.has(contractKey)) {
        contractsMap.set(contractKey, {
          contract: {
            contractNumber: material.contractNumber,
            contractItemPositionNumber: material.contractItemPositionNumber
          },
          hasValidMaterial: !materialOption.invalid
        })
      } else {
        // Update hasValidMaterial if we find at least one valid material
        const existing = contractsMap.get(contractKey)!
        if (!materialOption.invalid) {
          existing.hasValidMaterial = true
        }
      }
    })
  })

  // Convert map values to array, filter out contracts with only invalid materials, and sort
  return Array.from(contractsMap.values())
    .filter((entry) => entry.hasValidMaterial)
    .map((entry) => entry.contract)
    .sort((a, b) => a.contractNumber.localeCompare(b.contractNumber))
}

/**
 * Transforms materials from selectedSite into SplitLoadMaterial array
 * @param selectedSite - The selected site containing materials dictionary
 * @param includeInvalid - Whether to include invalid materials (default: true)
 * @returns Array of SplitLoadMaterial
 */
export const transformSiteMaterialsToSplitLoadMaterials = (
  selectedSite: OrderIntakeOption,
  includeInvalid: boolean = true
): SplitLoadMaterial[] => {
  const materials: SplitLoadMaterial[] = []

  // Iterate through the materials dictionary
  // Key is materialEnteredNumber, value is array of OrderIntakeMaterialOptionPayload
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Object.entries(selectedSite.materials).forEach(([_materialKey, materialOptions]) => {
    // Each materialEnteredNumber can have multiple options (different plants/contracts)
    materialOptions.forEach((materialOption) => {
      // Skip invalid materials if requested
      if (!includeInvalid && materialOption.invalid) {
        return
      }

      const splitLoadMaterial: SplitLoadMaterial = {
        // Todo: Check if this combination of fields is unique for ID
        id: `${materialOption.material.materialNumber}-${materialOption.plant.plantNumber}-${materialOption.material.contractNumber}-${materialOption.material.contractItemPositionNumber}`,
        materialNumber: materialOption.material.materialNumber,
        materialDescription: materialOption.material.materialDescription,
        // Note: materialType will be added by backend team later
        // For now, hardcoding to 'bagged' as requested
        materialType: materialOption.material.packagingType === 'V' ? 'bagged' : 'bulk',
        contractNumber: materialOption.material.contractNumber,
        contractItemPositionNumber: materialOption.material.contractItemPositionNumber,
        capacity: {
          quantity: materialOption.truckCapacity.capacity,
          quantityType: materialOption.material.quantityType
        },
        truckCapacity: materialOption.truckCapacity,
        bomMaterialNumber: materialOption.material.bomMaterials?.[0]?.bomMaterialNumber,
        bomMaterialPositionNumber:
          materialOption.material.bomMaterials?.[0]?.bomMaterialPositionNumber,
        bomMaterials: materialOption.material.bomMaterials, // Include all pallet size options
        deactivationReason: materialOption.deactivationReason || '',
        plant: materialOption.plant,
        invalid: materialOption.invalid
      }

      materials.push(splitLoadMaterial)
    })
  })

  return materials
}

/**
 * Groups split load materials by plant
 * @param materials - Array of SplitLoadMaterial
 * @returns Dictionary of materials grouped by plant number
 */
export const groupMaterialsByPlant = (
  materials: SplitLoadMaterial[]
): Record<string, SplitLoadMaterial[]> => {
  return materials.reduce(
    (acc, material) => {
      // Extract plant number from the composite ID
      const plantNumber = material.id.split('-')[1]
      if (!acc[plantNumber]) {
        acc[plantNumber] = []
      }
      acc[plantNumber].push(material)
      return acc
    },
    {} as Record<string, SplitLoadMaterial[]>
  )
}

/**
 * Groups split load materials by contract
 * @param materials - Array of SplitLoadMaterial
 * @returns Dictionary of materials grouped by contract number
 */
export const groupMaterialsByContract = (
  materials: SplitLoadMaterial[]
): Record<string, SplitLoadMaterial[]> => {
  return materials.reduce(
    (acc, material) => {
      const contractNumber = material.contractNumber
      if (!acc[contractNumber]) {
        acc[contractNumber] = []
      }
      acc[contractNumber].push(material)
      return acc
    },
    {} as Record<string, SplitLoadMaterial[]>
  )
}

/**
 * Filters materials to only return valid ones
 * @param materials - Array of SplitLoadMaterial
 * @returns Array of valid materials
 */
export const getValidMaterials = (materials: SplitLoadMaterial[]): SplitLoadMaterial[] => {
  return materials.filter((material) => !material.invalid)
}

/**
 * Filters materials to only return invalid ones
 * @param materials - Array of SplitLoadMaterial
 * @returns Array of invalid materials
 */
export const getInvalidMaterials = (materials: SplitLoadMaterial[]): SplitLoadMaterial[] => {
  return materials.filter((material) => material.invalid)
}
